import { z } from 'zod';
import { validatedGet, validatedPost, validatedPut, validatedDelete, apiPost } from '../api-client.ts';
import { LavoratoreCompletoSchema, PeriodoStoricoSchema, DisdettaSchema } from '@/types/entities/index.ts';
import { DelegaArricchitaSchema } from '@/types/api-responses.ts';
import { OkResult } from './shared.ts';

const RicercaResult = z.object({
  risultati: z.array(LavoratoreCompletoSchema),
  count: z.number(),
});

const StoricoResult = z.object({
  storico: z.array(PeriodoStoricoSchema),
});

const DelegheLavResult = z.object({
  deleghe: z.array(DelegaArricchitaSchema),
});

const DisdetteLavResult = z.object({
  disdette: z.array(DisdettaSchema),
});

const IscrizioniAltriSettoriLavResult = z.object({
  iscrizioni: z.array(z.object({
    id: z.number(),
    categoria: z.string(),
    categoriaNome: z.string().nullable(),
    data_iscrizione: z.string(),
    data_cessazione: z.string().nullable(),
    attivo: z.union([z.literal(0), z.literal(1)]),
    motivo_cessazione: z.string().nullable(),
    inviata_statini: z.union([z.literal(0), z.literal(1)]),
    impresa: z.object({ id: z.number(), ragioneSociale: z.string().nullable() }).nullable(),
  }).passthrough()),
});

const ListaResult = z.object({
  lavoratori: z.array(LavoratoreCompletoSchema),
  count: z.number(),
  page: z.number(),
  pageSize: z.number(),
  totalPages: z.number(),
});

interface ListaLavoratoriParams {
  [key: string]: string | number | undefined;
  page?: number;
  limit?: number;
  sort?: string;
  sortDir?: 'asc' | 'desc';
  q?: string;
  cassa?: string;
  sindacato?: string;
  statoLavoratore?: string;
  cognome?: string; cognomeOp?: string;
  nome?: string; nomeOp?: string;
  codiceFiscale?: string; codiceFiscaleOp?: string;
  comuneNascita?: string; comuneNascitaOp?: string;
  comuneResidenza?: string; comuneResidenzaOp?: string;
  viaResidenza?: string; viaResidenzaOp?: string;
  codce?: string; codceOp?: string;
  dataNascita?: string; dataNascitaOp?: string;
  dataNascitaDa?: string; dataNascitaA?: string;
  soloConIscrizioneAltriSettoriAttiva?: '1';
  categoriaIscrizione?: string;
}

const DatiCfSchema = z.object({
  sesso: z.enum(['M', 'F']),
  dataNascita: z.string(),
  codiceBelfiore: z.string(),
  luogoNascita: z.string().optional(),
  provinciaNascita: z.string().optional(),
  lavoratore: LavoratoreCompletoSchema.nullable(),
});

export const lavoratoriApi = {
  lista: (params: ListaLavoratoriParams = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') qs.set(k, String(v));
    }
    const url = `lavoratori/lista${qs.toString() ? '?' + qs.toString() : ''}`;
    return validatedGet(url, ListaResult);
  },

  decodificaCf: (cf: string) =>
    validatedGet(`lavoratori/decodifica-cf?cf=${encodeURIComponent(cf)}`, DatiCfSchema),

  cerca: (q: string, limit = 50) =>
    validatedGet(`lavoratori?q=${encodeURIComponent(q)}&limit=${limit}`, RicercaResult),

  dettaglio: (id: number) =>
    validatedGet(`lavoratori/${id}`, LavoratoreCompletoSchema),

  crea: (dati: Record<string, unknown>) =>
    validatedPost('lavoratori', dati, LavoratoreCompletoSchema),

  creaORecupera: async (dati: Record<string, unknown>): Promise<
    | { ok: true; data: z.infer<typeof LavoratoreCompletoSchema>; duplicato: boolean }
    | { ok: false; error: string }
  > => {
    const result = await apiPost<unknown>('lavoratori', dati);
    if (result.ok) {
      const parsed = LavoratoreCompletoSchema.safeParse(result.data);
      if (!parsed.success) return { ok: false, error: 'Risposta API non conforme' };
      return { ok: true, data: parsed.data, duplicato: false };
    }
    if (result.status === 409 && result.body && typeof result.body === 'object') {
      const b = result.body as Record<string, unknown>;
      const parsed = LavoratoreCompletoSchema.safeParse(b.lavoratore);
      if (parsed.success) return { ok: true, data: parsed.data, duplicato: true };
    }
    return result;
  },

  aggiorna: (id: number, dati: Record<string, unknown>) =>
    validatedPut(`lavoratori/${id}`, dati, LavoratoreCompletoSchema),

  elimina: (id: number) =>
    validatedDelete(`lavoratori/${id}`, OkResult),

  deleghe: (id: number) =>
    validatedGet(`lavoratori/${id}/deleghe`, DelegheLavResult),

  disdette: (id: number) =>
    validatedGet(`lavoratori/${id}/disdette`, DisdetteLavResult),

  storico: (id: number) =>
    validatedGet(`lavoratori/${id}/storico`, StoricoResult),

  iscrizioniAltriSettori: (id: number) =>
    validatedGet(`lavoratori/${id}/iscrizioni-altri-settori`, IscrizioniAltriSettoriLavResult),
};
